import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Send, Loader2, Sparkles, Paperclip, X, Plus,
  Check, FileText, ChevronRight, ChevronDown, Trash2,
  PencilLine, EyeOff, Eye, CheckCircle2, AlertCircle,
  MessageSquare, Search
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ICON_ACCION = {
  crear: <Plus className="w-4 h-4" />,
  editar: <PencilLine className="w-4 h-4" />,
  desactivar: <EyeOff className="w-4 h-4" />,
  activar: <Eye className="w-4 h-4" />,
};

const COLOR_ACCION = {
  crear: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  editar: 'bg-blue-50 text-blue-700 border-blue-300',
  desactivar: 'bg-amber-50 text-amber-700 border-amber-300',
  activar: 'bg-violet-50 text-violet-700 border-violet-300',
};

export default function AyniChatAsistente() {
  const [sesiones, setSesiones] = useState([]);
  const [sesionActiva, setSesionActiva] = useState(null);
  const [cargandoSesion, setCargandoSesion] = useState(false);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [adjuntos, setAdjuntos] = useState([]);
  const [subiendoArchivo, setSubiendoArchivo] = useState(false);
  const [error, setError] = useState('');
  // Conversaciones de Ayni adjuntas (para inyectar al contexto)
  const [convsAdjuntas, setConvsAdjuntas] = useState([]); // [{id, telefono, mensaje_usuario, matriculado, ...}]
  const [mostrarBuscadorConvs, setMostrarBuscadorConvs] = useState(false);
  const [busquedaConvs, setBusquedaConvs] = useState('');
  const [resultadosConvs, setResultadosConvs] = useState([]);
  const [buscandoConvs, setBuscandoConvs] = useState(false);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchSesiones = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/ia-training/asistente/sesiones`, { headers });
      setSesiones(r.data || []);
    } catch (err) {
      console.error(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchSesiones(); }, [fetchSesiones]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [sesionActiva?.mensajes?.length, enviando]);

  const abrirSesion = async (id) => {
    setCargandoSesion(true);
    setError('');
    try {
      const r = await axios.get(`${API_URL}/ia-training/asistente/sesion/${id}`, { headers });
      setSesionActiva(r.data);
    } catch (err) {
      setError('No se pudo cargar la conversación.');
    } finally {
      setCargandoSesion(false);
    }
  };

  const nuevaSesion = async () => {
    setError('');
    try {
      const r = await axios.post(`${API_URL}/ia-training/asistente/sesion/nueva`, {}, { headers });
      setSesionActiva({ ...r.data, mensajes: [] });
      setAdjuntos([]);
      setTexto('');
      fetchSesiones();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || err.message;
      setError(
        status === 404
          ? 'El backend en producción todavía no tiene el módulo del asistente. Apretá Deploy para subir el cambio de backend.'
          : `No se pudo crear una nueva conversación. ${status ? `(HTTP ${status})` : ''} ${detail || ''}`.trim()
      );
    }
  };

  const borrarSesion = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm('¿Borrar esta conversación?')) return;
    try {
      await axios.delete(`${API_URL}/ia-training/asistente/sesion/${id}`, { headers });
      if (sesionActiva?.id === id) setSesionActiva(null);
      fetchSesiones();
    } catch (err) {
      // noop
    }
  };

  const adjuntarArchivo = async (file) => {
    if (!file) return;
    setSubiendoArchivo(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(
        `${API_URL}/ia-training/asistente/adjuntar-archivo`,
        fd,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      setAdjuntos((p) => [...p, r.data]);
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo subir el archivo.');
    } finally {
      setSubiendoArchivo(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const buscarConversacionesAyni = async (q) => {
    setBuscandoConvs(true);
    try {
      const r = await axios.get(`${API_URL}/ia-training/asistente/conversaciones-ayni`, {
        params: { q: q || '', limite: 30 },
        headers
      });
      setResultadosConvs(r.data?.conversaciones || []);
    } catch (err) {
      console.error(err);
      setResultadosConvs([]);
    } finally {
      setBuscandoConvs(false);
    }
  };

  const adjuntarConv = (conv) => {
    if (convsAdjuntas.find(c => c.id === conv.id)) return;
    setConvsAdjuntas(prev => [...prev, conv]);
  };

  const quitarConv = (id) => {
    setConvsAdjuntas(prev => prev.filter(c => c.id !== id));
  };

  const enviarMensaje = async () => {
    if (!sesionActiva) {
      await nuevaSesion();
      return;
    }
    const t = texto.trim();
    if (!t && adjuntos.length === 0 && convsAdjuntas.length === 0) return;
    setEnviando(true);
    setError('');

    const tmpUser = {
      id: 'tmp-' + Date.now(),
      rol: 'user',
      texto: t,
      archivos: adjuntos.map(({ texto_extraido, ...rest }) => rest),
      ts: new Date().toISOString(),
    };
    setSesionActiva((p) => ({ ...p, mensajes: [...(p?.mensajes || []), tmpUser] }));

    try {
      await axios.post(`${API_URL}/ia-training/asistente/mensaje`, {
        sesion_id: sesionActiva.id,
        texto: t,
        archivos_adjuntos: adjuntos,
        conversaciones_inyectadas: convsAdjuntas.map(c => c.id),
      }, { headers });
      const full = await axios.get(`${API_URL}/ia-training/asistente/sesion/${sesionActiva.id}`, { headers });
      setSesionActiva(full.data);
      setTexto('');
      setAdjuntos([]);
      setConvsAdjuntas([]);
      fetchSesiones();
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo enviar el mensaje.');
      setSesionActiva((p) => ({ ...p, mensajes: (p?.mensajes || []).filter((m) => m.id !== tmpUser.id) }));
    } finally {
      setEnviando(false);
    }
  };

  const aplicarAccion = async (mensaje, idx) => {
    const accion = mensaje.acciones_propuestas[idx];
    try {
      await axios.post(`${API_URL}/ia-training/asistente/aplicar-accion`, {
        sesion_id: sesionActiva.id,
        accion,
      }, { headers });
      await axios.post(`${API_URL}/ia-training/asistente/marcar-accion-aplicada`, {
        sesion_id: sesionActiva.id,
        mensaje_id: mensaje.id,
        accion_idx: idx,
        estado: 'aplicada',
      }, { headers });
      const full = await axios.get(`${API_URL}/ia-training/asistente/sesion/${sesionActiva.id}`, { headers });
      setSesionActiva(full.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo aplicar la acción.');
    }
  };

  const rechazarAccion = async (mensaje, idx) => {
    try {
      await axios.post(`${API_URL}/ia-training/asistente/marcar-accion-aplicada`, {
        sesion_id: sesionActiva.id,
        mensaje_id: mensaje.id,
        accion_idx: idx,
        estado: 'rechazada',
      }, { headers });
      const full = await axios.get(`${API_URL}/ia-training/asistente/sesion/${sesionActiva.id}`, { headers });
      setSesionActiva(full.data);
    } catch (err) {/* noop */}
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      enviarMensaje();
    }
  };

  return (
    <div className="grid grid-cols-12 gap-4" style={{ minHeight: '70vh' }} data-testid="ayni-chat-asistente">
      {/* Sidebar de sesiones */}
      <div className="col-span-12 md:col-span-3">
        <Card className="h-full">
          <CardContent className="p-3 space-y-2 h-full flex flex-col">
            <Button
              onClick={nuevaSesion}
              size="sm"
              className="w-full bg-violet-600 hover:bg-violet-700"
              data-testid="ayni-chat-nueva-sesion"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Nueva conversación
            </Button>
            <div className="text-xs text-gray-500 px-1 pt-1 uppercase tracking-wide">Recientes</div>
            <div className="space-y-1 overflow-y-auto flex-1">
              {sesiones.length === 0 && (
                <p className="text-xs text-gray-400 px-1">Sin conversaciones aún.</p>
              )}
              {sesiones.map((s) => (
                <button
                  key={s.id}
                  onClick={() => abrirSesion(s.id)}
                  className={`group w-full text-left px-2 py-1.5 rounded-md text-sm flex items-center justify-between ${
                    sesionActiva?.id === s.id ? 'bg-violet-100 text-violet-900' : 'hover:bg-gray-100 text-gray-700'
                  }`}
                  data-testid={`ayni-sesion-${s.id}`}
                >
                  <span className="truncate">{s.titulo || 'Sin título'}</span>
                  <Trash2
                    onClick={(e) => borrarSesion(s.id, e)}
                    className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 flex-shrink-0 ml-2"
                  />
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Panel chat */}
      <div className="col-span-12 md:col-span-9">
        <Card className="h-full flex flex-col">
          <CardContent className="p-0 flex flex-col h-full">
            <div className="border-b px-4 py-3 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-600" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900">Chatear con Ayni asistente</h3>
                <p className="text-xs text-gray-500">
                  Decile en lenguaje natural qué cambios hacerle a Ayni. Cada cambio se aplica con tu confirmación.
                </p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4" style={{ maxHeight: '60vh' }}>
              {cargandoSesion ? (
                <div className="flex justify-center pt-8"><Loader2 className="w-6 h-6 animate-spin text-violet-600" /></div>
              ) : !sesionActiva ? (
                <EstadoVacio onIniciar={nuevaSesion} />
              ) : (
                <>
                  {(sesionActiva.mensajes || []).map((m) => (
                    <MensajeRow
                      key={m.id}
                      m={m}
                      onAplicar={(idx) => aplicarAccion(m, idx)}
                      onRechazar={(idx) => rechazarAccion(m, idx)}
                    />
                  ))}
                  {enviando && (
                    <div className="flex items-center gap-2 text-sm text-gray-500 pl-9">
                      <Loader2 className="w-4 h-4 animate-spin" /> Ayni está pensando…
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Adjuntos y conversaciones */}
            {(adjuntos.length > 0 || convsAdjuntas.length > 0) && (
              <div className="border-t px-4 py-2 flex flex-wrap gap-2">
                {adjuntos.map((a, i) => (
                  <Badge key={'f' + i} variant="outline" className="gap-1 pl-2 pr-1 py-1 bg-gray-50">
                    <FileText className="w-3 h-3" />
                    <span className="text-xs truncate max-w-[200px]">{a.nombre}</span>
                    <button onClick={() => setAdjuntos((p) => p.filter((_, j) => j !== i))} className="ml-1 text-gray-400 hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
                {convsAdjuntas.map((c) => (
                  <Badge key={'c' + c.id} variant="outline" className="gap-1 pl-2 pr-1 py-1 bg-violet-50 border-violet-300 text-violet-800">
                    <MessageSquare className="w-3 h-3" />
                    <span className="text-xs truncate max-w-[220px]">
                      {c.matriculado?.nombre
                        ? `${c.matriculado.nombre} ${c.matriculado.apellido || ''}`.trim()
                        : c.telefono || 'conversación'}
                    </span>
                    <button onClick={() => quitarConv(c.id)} className="ml-1 text-violet-400 hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}

            {error && (
              <div className="px-4 py-2 bg-red-50 border-t border-red-200 text-sm text-red-700 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
              </div>
            )}

            <div className="border-t p-3">
              <div className="flex items-end gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.txt,.md,image/*"
                  onChange={(e) => adjuntarArchivo(e.target.files?.[0])}
                />
                <Button
                  variant="outline"
                  size="icon"
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={subiendoArchivo}
                  data-testid="ayni-chat-adjuntar"
                  title="Adjuntar archivo (PDF, txt, imagen)"
                >
                  {subiendoArchivo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  type="button"
                  onClick={() => {
                    setMostrarBuscadorConvs(true);
                    if (resultadosConvs.length === 0) buscarConversacionesAyni('');
                  }}
                  data-testid="ayni-chat-buscar-convs"
                  title="Adjuntar conversación real de Ayni"
                  className="text-violet-600 border-violet-300 hover:bg-violet-50"
                >
                  <MessageSquare className="w-4 h-4" />
                </Button>
                <Textarea
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder='Ej: "Agregale que los miércoles abrimos hasta las 14" · Ctrl+Enter para enviar'
                  className="flex-1 min-h-[60px] max-h-[200px] resize-none"
                  data-testid="ayni-chat-input"
                />
                <Button
                  onClick={enviarMensaje}
                  disabled={enviando || (!texto.trim() && adjuntos.length === 0 && convsAdjuntas.length === 0)}
                  className="bg-violet-600 hover:bg-violet-700"
                  data-testid="ayni-chat-enviar"
                >
                  {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modal de búsqueda de conversaciones de Ayni */}
      {mostrarBuscadorConvs && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setMostrarBuscadorConvs(false)}
          data-testid="ayni-buscador-convs-modal"
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-violet-600" />
                <h3 className="font-semibold">Conversaciones reales de Ayni</h3>
              </div>
              <button
                onClick={() => setMostrarBuscadorConvs(false)}
                className="text-gray-400 hover:text-gray-600"
                data-testid="ayni-buscador-cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-4 py-3 border-b">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar por teléfono, matrícula, DNI, nombre o texto del mensaje…"
                    value={busquedaConvs}
                    onChange={(e) => setBusquedaConvs(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') buscarConversacionesAyni(busquedaConvs); }}
                    className="w-full border rounded-md pl-9 pr-3 py-2 text-sm"
                    data-testid="ayni-buscador-input"
                  />
                </div>
                <Button
                  size="sm"
                  onClick={() => buscarConversacionesAyni(busquedaConvs)}
                  disabled={buscandoConvs}
                  data-testid="ayni-buscador-submit"
                >
                  {buscandoConvs ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buscar'}
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Tocá una conversación para adjuntarla al chat. El entrenador podrá leerla y ayudarte a corregir lo que Ayni respondió mal.
              </p>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {buscandoConvs ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-violet-600" /></div>
              ) : resultadosConvs.length === 0 ? (
                <p className="text-center text-sm text-gray-400 py-8">Sin resultados.</p>
              ) : (
                resultadosConvs.map((c) => {
                  const yaAdjunta = !!convsAdjuntas.find(x => x.id === c.id);
                  return (
                    <div
                      key={c.id}
                      className={`border rounded-md p-3 cursor-pointer transition-colors ${
                        yaAdjunta ? 'bg-violet-50 border-violet-300' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => !yaAdjunta && adjuntarConv(c)}
                      data-testid={`ayni-conv-result-${c.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                            <span className="font-medium text-gray-700">
                              {c.matriculado?.nombre
                                ? `${c.matriculado.nombre} ${c.matriculado.apellido || ''}`.trim()
                                : c.telefono || 'Sin identificar'}
                            </span>
                            {c.matriculado?.matricula && (
                              <Badge variant="outline" className="text-xs">Mat. {c.matriculado.matricula}</Badge>
                            )}
                            <span>·</span>
                            <span>{c.telefono}</span>
                            {c.timestamp && <span>· {c.timestamp.slice(0, 16).replace('T', ' ')}</span>}
                            {c.tiene_correccion && (
                              <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">Con corrección</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-800">
                            <strong>Usuario:</strong> {c.mensaje_usuario}
                          </p>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            <strong>Ayni:</strong> {c.respuesta_ia}
                          </p>
                        </div>
                        {yaAdjunta ? (
                          <CheckCircle2 className="w-5 h-5 text-violet-600 flex-shrink-0" />
                        ) : (
                          <Plus className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div className="border-t px-4 py-3 flex justify-between items-center">
              <span className="text-sm text-gray-500">
                {convsAdjuntas.length} adjunta{convsAdjuntas.length !== 1 ? 's' : ''}
              </span>
              <Button onClick={() => setMostrarBuscadorConvs(false)} data-testid="ayni-buscador-listo">
                Listo
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EstadoVacio({ onIniciar }) {
  const ejemplos = [
    'Agregale que los miércoles abrimos hasta las 14hs.',
    'Cuando le pregunten por el plan de pagos, que diga que es de 3 cuotas con 10% de interés.',
    'Mostrame qué sabe sobre certificados.',
    'Olvidate todo lo viejo que decía sobre la rifa 2025.',
  ];
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center">
      <Sparkles className="w-10 h-10 text-violet-300 mb-3" />
      <h3 className="text-lg font-semibold text-gray-800">Empezá a entrenar a Ayni hablando</h3>
      <p className="text-sm text-gray-500 max-w-md mt-1">
        Decile qué cosas tiene que saber, modificá lo que ya sabe, sumá archivos. Cada acción se aplica con tu confirmación.
      </p>
      <Button onClick={onIniciar} className="mt-4 bg-violet-600 hover:bg-violet-700">
        <Plus className="w-4 h-4 mr-1.5" /> Nueva conversación
      </Button>
      <div className="mt-6 w-full max-w-md space-y-1.5">
        <p className="text-xs uppercase tracking-wide text-gray-400">Ejemplos para empezar</p>
        {ejemplos.map((e, i) => (
          <div key={i} className="text-left text-xs text-gray-600 bg-gray-50 rounded-md px-3 py-2 border">{e}</div>
        ))}
      </div>
    </div>
  );
}

function MensajeRow({ m, onAplicar, onRechazar }) {
  const [expanded, setExpanded] = useState({});
  if (m.rol === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] bg-violet-600 text-white rounded-2xl rounded-tr-md px-3.5 py-2 text-sm whitespace-pre-wrap" data-testid={`msg-user-${m.id}`}>
          {m.texto}
          {m.archivos?.length > 0 && (
            <div className="mt-2 space-y-1">
              {m.archivos.map((a, i) => (
                <a key={i} href={a.url} target="_blank" rel="noreferrer" className="block text-xs underline opacity-90 hover:opacity-100">
                  📎 {a.nombre}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // assistant
  const acciones = m.acciones_propuestas || [];
  const estados = m.acciones_estado || [];

  return (
    <div className="flex justify-start">
      <div className="max-w-[90%] w-full space-y-2" data-testid={`msg-assist-${m.id}`}>
        <div className="bg-gray-100 text-gray-900 rounded-2xl rounded-tl-md px-3.5 py-2 text-sm whitespace-pre-wrap">
          {m.texto || (m.raw ? '(Respuesta sin acciones — ver raw)' : '')}
        </div>
        {acciones.map((a, idx) => {
          const estado = estados[idx] || 'pendiente';
          const isOpen = expanded[idx];
          return (
            <div key={idx} className={`border rounded-lg px-3 py-2 ${COLOR_ACCION[a.tipo] || 'bg-gray-50'}`} data-testid={`accion-${idx}`}>
              <div className="flex items-start gap-2">
                <div className="mt-0.5">{ICON_ACCION[a.tipo] || <Sparkles className="w-4 h-4" />}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-wide font-medium opacity-80">{a.tipo}</div>
                  <div className="text-sm font-medium">{a.descripcion}</div>
                  <button
                    type="button"
                    onClick={() => setExpanded((p) => ({ ...p, [idx]: !isOpen }))}
                    className="text-xs underline mt-1 inline-flex items-center gap-0.5 opacity-80 hover:opacity-100"
                  >
                    {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    {isOpen ? 'Ocultar detalle' : 'Ver detalle'}
                  </button>
                  {isOpen && (
                    <pre className="mt-2 text-xs bg-white/70 border rounded p-2 max-h-[260px] overflow-auto whitespace-pre-wrap break-words" data-testid={`accion-${idx}-detalle`}>
{JSON.stringify(a.payload || {}, null, 2)}
                    </pre>
                  )}
                </div>
                <div className="flex-shrink-0">
                  {estado === 'aplicada' && (
                    <Badge className="bg-emerald-600 text-white gap-1"><CheckCircle2 className="w-3 h-3" /> Aplicada</Badge>
                  )}
                  {estado === 'rechazada' && (
                    <Badge variant="outline" className="text-gray-500">Rechazada</Badge>
                  )}
                  {estado === 'pendiente' && (
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => onRechazar(idx)} data-testid={`accion-${idx}-rechazar`}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" onClick={() => onAplicar(idx)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`accion-${idx}-aplicar`}>
                        <Check className="w-3.5 h-3.5 mr-1" /> Aplicar
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
